<template>
  <div>
    <p class="has-text-weight-bold is-size-7 has-text-grey mb-xs">Chatbot preview</p>
    <!-- Approximates the chatbot panel (OnboardingChatbot.vue) with Bulma/Buefy
         classes: header, message thread, disabled composer. -->
    <div class="border rounded-lg has-background-white">
      <!-- Header -->
      <div class="has-background-primary has-text-white rounded-t-lg pa-md">
        <div class="columns is-mobile is-vcentered is-marginless">
          <div class="column is-paddingless">
            <p class="has-text-weight-bold">{{ assistant.title || 'Supplier Assistant' }}</p>
            <p class="is-size-7">{{ assistant.subtitle || 'Guidance from your fabric supplier docs' }}</p>
          </div>
          <div class="column is-narrow is-paddingless">
            <b-icon icon="refresh" size="is-small" class="mr-sm" />
            <b-icon icon="close" size="is-small" />
          </div>
        </div>
      </div>
      <!-- Thread -->
      <div class="has-background-white-bis pa-md">
        <!-- The supplier's question -->
        <div class="columns is-mobile mb-sm">
          <div class="column is-3"></div>
          <div class="column is-flex is-justify-content-flex-end">
            <div class="has-background-primary has-text-white rounded-lg px-md py-sm is-size-7">
              {{ entry.title || 'Your question' }}
            </div>
          </div>
        </div>
        <!-- The answer card -->
        <div class="columns is-mobile">
          <div class="column is-11">
            <div class="has-background-white-ter rounded-lg px-md py-sm is-size-7">
              <p class="has-text-weight-bold mb-xs">{{ entry.title || 'Question title' }}</p>
              <p class="is-size-7 is-uppercase has-text-grey">{{ entry.group || 'Group' }}</p>
              <div v-if="answer" class="content is-small mt-sm" v-html="answer"></div>
              <p v-else class="mt-sm has-text-grey"><i>Your answer will appear here.</i></p>
            </div>
            <div v-if="relatedChips.length" class="tags mt-sm">
              <span v-for="(title, index) in relatedChips" :key="index" class="tag is-primary is-light is-rounded">{{ title }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Composer -->
      <div class="border-t rounded-b-lg pa-sm">
        <b-field class="is-marginless">
          <b-input placeholder="Ask about onboarding..." rounded expanded disabled />
          <p class="control">
            <b-button type="is-primary" icon-left="send" disabled />
          </p>
        </b-field>
      </div>
    </div>
    <p class="is-size-7 mt-sm" :class="status === 'live' ? 'has-text-success' : 'has-text-grey'">
      <template v-if="status === 'live'">
        <b-icon icon="eye" size="is-small" /> Live &mdash; suppliers see this when they search for it or pick it from the menu.
      </template>
      <template v-else>
        <b-icon icon="eye-off" size="is-small" /> Draft &mdash; hidden from suppliers until switched to Live.
      </template>
    </p>
    <p class="is-size-7 has-text-grey">Up to three related questions appear as chips after the answer.</p>
  </div>
</template>

<script>
// Mirrors assistant/helpers.py ALLOWED_TAGS / ALLOWED_ATTRIBUTES (bleach with
// strip=True) so the preview shows what the API will actually store.
const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h3', 'h4', 'i', 'img', 'li',
  'ol', 'p', 'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul'
]
const ALLOWED_ATTRIBUTES = {
  a: ['href', 'title', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height'],
  '*': ['class']
}
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:']

const isSafeUrl = (value) => {
  try {
    const url = new URL(value, window.location.origin)
    return ALLOWED_PROTOCOLS.includes(url.protocol)
  } catch (e) {
    return false
  }
}

const sanitizeElement = (element) => {
  // Children first, so unwrapping a disallowed parent leaves clean siblings.
  Array.from(element.childNodes).forEach(child => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      sanitizeElement(child)
    } else if (child.nodeType !== Node.TEXT_NODE) {
      element.removeChild(child)
    }
  })
  const tag = element.tagName.toLowerCase()
  if (!ALLOWED_TAGS.includes(tag)) {
    // strip=True: drop the tag, keep its (already sanitized) content.
    const parent = element.parentNode
    while (element.firstChild) parent.insertBefore(element.firstChild, element)
    parent.removeChild(element)
    return
  }
  const allowed = (ALLOWED_ATTRIBUTES[tag] || []).concat(ALLOWED_ATTRIBUTES['*'])
  Array.from(element.attributes).forEach(attribute => {
    const name = attribute.name.toLowerCase()
    const isUrl = (tag === 'a' && name === 'href') || (tag === 'img' && name === 'src')
    if (!allowed.includes(name) || (isUrl && !isSafeUrl(attribute.value))) {
      element.removeAttribute(attribute.name)
    }
  })
}

const sanitize = (html) => {
  if (!html) return ''
  const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html')
  const root = doc.body.firstChild
  Array.from(root.children).forEach(sanitizeElement)
  return root.innerHTML
}

export default {
  computed: {
    answer () {
      return sanitize(this.entry.answer)
    },
    // The chatbot shows the first three related entries as chips when an answer
    // is reached via search (pushAnswer in OnboardingChatbot.vue).
    relatedChips () {
      return (this.entry.related || []).slice(0, 3)
    }
  },
  props: {
    // { title, group, answer, related: [titles] } — the chatbot's entry shape.
    entry: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      default: 'draft'
    },
    assistant: {
      type: Object,
      default: () => ({})
    }
  }
}
</script>
